import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppMode } from '../context/AppModeContext';

export default function Navbar() {
  const { user, logout, isAuthenticated, isAnyAdmin, isSystemAdmin, hasFeature } = useAuth();
  const { isMobileDevice, clearAppMode } = useAppMode();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const orgMenuRef = useRef(null);
  const adminMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setOrgMenuOpen(false);
    setAdminMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target)) {
        setOrgMenuOpen(false);
      }
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target)) {
        setAdminMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated) return null;

  const hasOrgItems = hasFeature('USER_MANAGEMENT') || hasFeature('ORG_LICENCES')
    || hasFeature('DOCUMENTS') || hasFeature('TRAINING');

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <Link to="/" onClick={closeMenu}>
          <img src="/logo-pocketrams.png" alt="PocketRAMS" className="navbar-logo" />
        </Link>
      </div>
      <button className="navbar-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
        <span /><span /><span />
      </button>
      <div className={`navbar-menu${menuOpen ? ' open' : ''}`}>
        <div className="navbar-links">
          {hasFeature('REPORTS') && <Link to="/reports" onClick={closeMenu}>Reports</Link>}
          {hasFeature('LOCATIONS') && <Link to="/sites" onClick={closeMenu}>Sites</Link>}

          {hasOrgItems && (
            <div className="nav-dropdown" ref={orgMenuRef}>
              <button
                className="nav-dropdown-toggle"
                onClick={() => { setOrgMenuOpen(!orgMenuOpen); setAdminMenuOpen(false); }}
              >
                Organisation
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {orgMenuOpen && (
                <div className="nav-dropdown-menu">
                  {hasFeature('USER_MANAGEMENT') && <Link to="/organisation-users" onClick={closeMenu}>Users</Link>}
                  {hasFeature('DOCUMENTS') && <Link to="/org-documents" onClick={closeMenu}>Documents</Link>}
                  {hasFeature('TRAINING') && <Link to="/training-records" onClick={closeMenu}>Training</Link>}
                  {hasFeature('ORG_LICENCES') && <Link to="/org-licences" onClick={closeMenu}>Licences</Link>}
                </div>
              )}
            </div>
          )}

          {isSystemAdmin && (
            <div className="nav-dropdown" ref={adminMenuRef}>
              <button
                className="nav-dropdown-toggle"
                onClick={() => { setAdminMenuOpen(!adminMenuOpen); setOrgMenuOpen(false); }}
              >
                Admin
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {adminMenuOpen && (
                <div className="nav-dropdown-menu">
                  <Link to="/organisations" onClick={closeMenu}>Organisations</Link>
                  <Link to="/licences" onClick={closeMenu}>Licence Admin</Link>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="navbar-user" ref={userMenuRef}>
          <button
            className="user-icon-btn"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            aria-label="User menu"
            title={user.firstName + ' ' + user.lastName}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
          {userMenuOpen && (
            <div className="user-dropdown">
              <div className="user-dropdown-name">{user.firstName} {user.lastName}</div>
              {isAnyAdmin && !isSystemAdmin && <Link to="/organisation-settings" onClick={closeMenu}>Organisation Settings</Link>}
              <Link to="/update-password" onClick={closeMenu}>Change Password</Link>
              {isAnyAdmin && <Link to="/profile-permissions" onClick={closeMenu}>Permissions</Link>}
              {isMobileDevice && <button onClick={() => { closeMenu(); clearAppMode(); }}>Data Entry Mode</button>}
              <button onClick={() => { closeMenu(); handleLogout(); }}>Logout</button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
